import React, { useEffect, useState } from 'react';
import LoadingSpinner from './LoadingSpinner';
import Button from './ui/button';

/**
 * LLMLoadingSpinner component
 *
 * A reusable loading spinner specifically for LLM operations (journey generation and editing).
 * Shows a fullscreen overlay with a spinner, a rotating status message, and a cancel button.
 *
 * The caller's `message` shows first, then the shared pipeline sentences rotate in one by
 * one; the last one holds until the request finishes (no looping — restarting the cycle on
 * a slow generation would read as fake progress).
 *
 * Props:
 * - message (string, optional): The opening status message. Defaults to 'Processing…'.
 * - onCancel (function, optional): Callback function when the cancel button is clicked.
 * - isVisible (boolean, optional): Whether to show the spinner. Defaults to true.
 */
interface LLMLoadingSpinnerProps {
  message?: string;
  onCancel?: () => void;
  isVisible?: boolean;
}

// Shared across all LLM waits (product backlog #32) — ordered like the real
// generation pipeline so the wait reads as progress, not filler.
const ROTATING_MESSAGES = [
  'Understanding your preferences',
  'Exploring cool places',
  'Calculating distances',
  'Exploring transport options',
  'Calculating the climate impact',
  'Creating stops in a smart order',
  'Putting everything together',
];

const ROTATE_INTERVAL_MS = 4000;

const LLMLoadingSpinner: React.FC<LLMLoadingSpinnerProps> = ({
  message = 'Processing…',
  onCancel,
  isVisible = true
}) => {
  // 0 = the caller's message; 1..N = ROTATING_MESSAGES[step - 1]. Holds at N.
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    setStep(0);
    const id = setInterval(
      () => setStep((prev) => Math.min(prev + 1, ROTATING_MESSAGES.length)),
      ROTATE_INTERVAL_MS
    );
    return () => clearInterval(id);
  }, [isVisible]);

  if (!isVisible) return null;

  // The ellipsis is rendered separately (animated), so strip any trailing one
  // from the caller's own message.
  const text = (step === 0 ? message : ROTATING_MESSAGES[step - 1]).replace(/(\.{3}|…)\s*$/, '');

  return (
    <LoadingSpinner
      fullscreen
      message={
        <span key={step} className="llm-loading-message">
          {text}
          <span className="llm-loading-dots" aria-hidden="true">
            <span>.</span>
            <span>.</span>
            <span>.</span>
          </span>
          {/* Inline CSS, same pattern as LoadingSpinner's spinner animation */}
          <style>
            {`
              .llm-loading-message {
                display: inline-block;
                animation: llm-message-in 0.4s ease;
              }
              @keyframes llm-message-in {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              .llm-loading-dots span {
                opacity: 0;
                animation-duration: 1.6s;
                animation-iteration-count: infinite;
              }
              .llm-loading-dots span:nth-child(1) { animation-name: llm-dot-1; }
              .llm-loading-dots span:nth-child(2) { animation-name: llm-dot-2; }
              .llm-loading-dots span:nth-child(3) { animation-name: llm-dot-3; }
              @keyframes llm-dot-1 { 0%, 24% { opacity: 0; } 25%, 100% { opacity: 1; } }
              @keyframes llm-dot-2 { 0%, 49% { opacity: 0; } 50%, 100% { opacity: 1; } }
              @keyframes llm-dot-3 { 0%, 74% { opacity: 0; } 75%, 100% { opacity: 1; } }
            `}
          </style>
        </span>
      }
    >
      {onCancel && (
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      )}
    </LoadingSpinner>
  );
};

export default LLMLoadingSpinner;
